"use client";

import { motion } from "framer-motion";
import { withPhoneLinks } from "@/components/PhoneText";

interface InfoSection {
  heading: string;
  content?: string;
  listItems?: { label: string; text: string }[];
  afterContent?: string;
  cta?: { text: string; href: string };
}

const defaultSections: InfoSection[] = [
  {
    heading: "Bortforsling",
    content:
      "Att bli av med skrymmande möbler, elektronik eller ett helt bohag behöver inte vara krångligt. Med Ungdomskraft får du snabb och smidig bortforsling – oavsett om det handlar om en enstaka soffa, ett helt dödsbo eller ett förråd som behöver tömmas. Vi tar hand om allt från tunga lyft till ansvarsfull sortering och återvinning, så att du slipper köra till tippen själv.\n\nVåra unga, energiska medarbetare hämtar ditt skräp direkt från hemmet, kontoret eller förrådet. Du visar bara vad som ska bort – vi bär, lastar och kör iväg allt åt dig.",
  },
  {
    heading: "Varför boka bortforsling med Ungdomskraft?",
    listItems: [
      {
        label: "Enkel bokning utan krångel -",
        text: "Boka din bortforsling på några minuter. Fyll i adress, välj datum och få pris direkt – slipp väntan på offert. Behöver du hjälp redan idag? Vi kan vara på plats redan inom 60 minuter för akuta behov. Självklart kan du även ringa in och boka på 08-517 082 81.",
      },
      {
        label: "Flexibla tider som passar dig -",
        text: "Vi vet att din tid är värdefull. Därför erbjuder vi bortforsling på tider som passar ditt schema – vardagar, kvällar och helger. Välj det datum och den tid som fungerar bäst för dig.",
      },
      {
        label: "Miljövänlig hantering och återvinning -",
        text: "Vi sorterar och återvinner så mycket som möjligt. Det som kan doneras skänks vidare till organisationer som Myrorna och Stadsmissionen. Resten hanteras ansvarsfullt och körs till godkänd återvinningsstation. Du kan även ha rätt till RUT-avdrag när vi transporterar saker till återanvändning.",
      },
      {
        label: "Försäkrat och tryggt -",
        text: "Alla uppdrag är försäkrade genom Trygg Hansa. Du kan känna dig trygg med att dina ägodelar och din fastighet är skyddade under hela processen – från lyft till transport.",
      },
    ],
  },
  {
    heading: "Vad kan vi forsla bort?",
    content:
      "Vi hanterar de flesta typer av skrymmande föremål och avfall. Oavsett om du behöver bli av med enstaka saker eller tömma ett helt utrymme hjälper vi dig:",
    listItems: [
      { label: "Möbler -", text: "soffor, sängar, bord, stolar, bokhyllor och garderober" },
      { label: "Vitvaror -", text: "tvättmaskiner, torktumlare, kylskåp, frysar och spisar" },
      { label: "Elektronik -", text: "TV-apparater, datorer, skärmar och mindre elavfall" },
      { label: "Grovsopor -", text: "madrasser, mattor, lampor och annat som inte ryms i vanliga sopkärlet" },
      { label: "Trädgårdsavfall -", text: "grenar, jord, sten och övrigt trädgårdsskräp" },
      { label: "Byggavfall -", text: "rivningsmaterial, gipsskivor och skräp efter mindre renoveringar" },
      { label: "Kontorsinventarier -", text: "skrivbord, kontorsstolar, arkivskåp och IT-utrustning" },
    ],
    afterContent:
      "Vi tar även emot blandade lass där allt möjligt ska bort samtidigt. Är du osäker på om vi kan hantera just ditt avfall? Hör av dig så hjälper vi dig.",
  },
  {
    heading: "Så fungerar det – bortforsling i tre enkla steg",
    listItems: [
      {
        label: "1. Beskriv ditt behov -",
        text: "Fyll i vårt bokningsformulär med adress, vad som ska forslas bort och önskat datum. Du får ett pris direkt – inga dolda kostnader.",
      },
      {
        label: "2. Vi kommer till dig -",
        text: "Våra ungdomar dyker upp på överenskommen tid, bär ut allt och lastar på bilen. Vi hanterar trapphus, hissar och trånga utrymmen – du behöver inte lyfta ett finger.",
      },
      {
        label: "3. Ansvarsfull hantering -",
        text: "Vi kör ditt avfall till rätt ställe. Det som går att återanvända skänks vidare, resten sorteras och lämnas på godkänd återvinningsstation. Allt hanteras enligt gällande regler så att så lite som möjligt hamnar på deponi.",
      },
    ],
  },
  {
    heading: "Erfarna bortforslare med lokalkännedom",
    content:
      "Våra ungdomar har gedigen erfarenhet av bortforsling. De är vana vid trånga trapphus, smala hissar och innergårdar som kräver extra planering. Oavsett om du bor i en villa, en lägenhet eller ett radhus vet de hur uppdraget ska genomföras på ett effektivt och säkert sätt.\n\nVi utför dagligen bortforsling av allt från enstaka möbler till kompletta lägenhetsröjningar och dödsbon. Den erfarenheten gör att vi kan ge dig ett exakt pris redan vid bokning och genomföra uppdraget utan fördröjningar.",
  },
  {
    heading: "Mer än bara bortforsling – du gör skillnad",
    content:
      "Det som gör Ungdomskraft unikt är att vi sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet. När du bokar bortforsling hos oss får du inte bara professionell hjälp med att hämta skräp och köra till tippen – du bidrar också till att unga människor kommer ut i arbetslivet och bygger sin framtid.\n\nVarje uppdrag bemannas av motiverade ungdomar som får lära sig allt från kundkontakt och logistik till fysiskt arbete och ansvarsfullt miljöarbete. Det är bortforsling som gör skillnad, varje bokning räknas.",
  },
  {
    heading: "Boka bortforsling idag",
    content:
      'Letar du efter bortforsling nära mig och vill ha en tjänst som kombinerar smidighet, pålitlighet och bra pris? Då är Ungdomskraft rätt val. Vi gör det enkelt att bli av med det du inte längre behöver – snabbt, tryggt och miljövänligt.\n\nFyll i formuläret så får du pris direkt. Inga offertförfrågningar – bara enkel bortforsling som dessutom gör skillnad. Självklart kan du även ringa in och boka bortforsling på 08-517 082 81.',
    cta: { text: "Boka bortforsling", href: "#priser" },
  },
];

interface ServiceInfoProps {
  heading?: string;
  sections?: InfoSection[];
}

export default function ServiceInfo({
  heading = "Bortforsling",
  sections = defaultSections,
}: ServiceInfoProps = {}) {
  return (
    <section id="bortforsling" className="py-8 sm:pt-10 sm:pb-24 bg-white">
      <div className="mx-auto max-w-screen-2xl px-6 xl:px-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-xl sm:text-2xl font-bold text-brand-dark">
            {heading}
          </h2>
          {sections[0]?.content &&
            sections[0].content.split("\n\n").map((p, i) => (
              <p key={i} className="mt-3 text-sm text-gray-600 leading-relaxed">
                {withPhoneLinks(p)}
              </p>
            ))}
        </motion.div>

        {sections.slice(1).map((section, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: 0.05 + idx * 0.05 }}
            className="mt-8"
          >
            <h3 className="text-base sm:text-lg font-bold text-brand-dark">
              {section.heading}
            </h3>
            {section.content &&
              section.content.split("\n\n").map((p, i) => (
                <p key={i} className="mt-3 text-sm text-gray-600 leading-relaxed">
                  {withPhoneLinks(p)}
                </p>
              ))}
            {section.listItems && (
              <ul className="mt-3 space-y-3 text-sm text-gray-600 leading-relaxed">
                {section.listItems.map((item, i) => (
                  <li key={i}>
                    <span className="font-medium text-brand-dark">
                      {item.label}
                    </span>{" "}
                    {withPhoneLinks(item.text)}
                  </li>
                ))}
              </ul>
            )}
            {section.afterContent &&
              section.afterContent.split("\n\n").map((p, i) => (
                <p key={i} className="mt-3 text-sm text-gray-600 leading-relaxed">
                  {withPhoneLinks(p)}
                </p>
              ))}
            {section.cta && (
              <a
                href={section.cta.href}
                className="mt-6 inline-block bg-brand-secondary text-white font-bold text-sm px-8 py-3 rounded-xl hover:brightness-110 transition-all"
              >
                {section.cta.text}
              </a>
            )}
          </motion.div>
        ))}
      </div>
    </section>
  );
}
